/**
 * Paiement comptant (design 4.4, maquette paiement.html).
 *
 * Recoit le PanierAEncaisser de l'ecran de vente tel quel : il le verifie
 * (verifierPanier du contrat, refus clair s'il est invalide) et n'en
 * recalcule rien (ni lignes ni taxes : seulement totals). Il ajoute l'arrondi
 * du comptant, le montant recu et la monnaie, puis enregistre la vente en une
 * transaction (store/sales.js).
 *
 * « Terminer la vente » reste desactive tant que le montant recu est plus
 * petit que le montant du, et se desactive des le premier clic : un double
 * clic n'envoie qu'une fois (et l'enregistrement est idempotent par
 * sale_uuid de toute facon).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChampMontant from './ChampMontant';
import PaveMontant from './PaveMontant';
import { ajouterChiffres, effacerChiffre } from './saisieMontant';
import { palette } from '../app/theme';
import { MONTANT_RECU_MAX_CENTS, arrondirComptant, monnaieARendre } from '../domain/cashRounding';
import { recordSale } from '../store/sales';
import { formatMontant, verifierPanier } from '../vente/contrat';

const CARD_SX = {
    bgcolor: palette.surface, border: `1px solid ${palette.border}`, borderRadius: '14px',
    boxShadow: '0 1px 3px rgba(15,23,42,.06)', p: 3,
};

const LABEL_SX = { fontSize: 18, fontWeight: 600, color: palette.textSecondary, mb: 1 };

const TILE_SX = { minHeight: 84, fontSize: 26, fontWeight: 700, borderRadius: '12px', borderWidth: 2, '&:hover': { borderWidth: 2 } };

/** Montants rapides : « Montant exact », 20 $, 50 $, 100 $. */
const BILLETS = [2000, 5000, 10000];

/** Message d'un refus d'enregistrement : quoi faire, pas ce qui s'est passe. */
export function messageErreurVente(error) {
    switch (error && error.code) {
        case 'QUART_FERME':
            return 'La caisse n\'est pas ouverte sur ce poste. Ouvrez la caisse, puis encaissez de nouveau.';
        case 'PANIER_INVALIDE':
        case 'POSTE_DIFFERENT':
            return 'Ce panier ne peut pas être encaissé. Retournez au panier ; si le problème continue, appelez le responsable.';
        case 'PAIEMENT_INCOMPLET':
            return 'Le montant reçu ne couvre pas le total à payer.';
        default:
            return 'La vente n\'a pas pu être enregistrée sur ce poste. Réessayez ; si le problème continue, appelez le responsable.';
    }
}

function PanierRefuse({ erreurs, onRetourVente }) {
    return (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 4 }}>
            <Box role="alert" data-testid="paiement-refuse" sx={{ ...CARD_SX, maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: palette.errorTint, color: palette.errorOnTint }}>
                <Typography component="h2" sx={{ fontSize: 26, fontWeight: 800 }}>Paiement impossible : panier incomplet</Typography>
                <Typography sx={{ fontSize: 18 }}>
                    Le panier reçu de l&apos;écran de vente est incomplet. Retournez au panier ; si le problème continue, appelez le responsable.
                </Typography>
                <Typography data-testid="paiement-refuse-details" sx={{ fontSize: 14, fontFamily: 'monospace' }}>
                    {erreurs.slice(0, 10).join(', ')}
                </Typography>
                <Button variant="outlined" color="inherit" startIcon={<ArrowBackIcon />} onClick={onRetourVente} sx={{ minHeight: 64, alignSelf: 'flex-start' }}>
                    Retour au panier
                </Button>
            </Box>
        </Box>
    );
}

export default function EcranPaiement({ db, panier, shiftId, onRetourVente, onVenteTerminee, now }) {
    const erreurs = useMemo(() => verifierPanier(panier), [panier]);
    const valide = erreurs.length === 0;
    const arrondi = useMemo(() => (valide ? arrondirComptant(panier.totals.total_cents) : null), [valide, panier]);

    const [recu, setRecu] = useState(0);
    const [rapide, setRapide] = useState(null);
    const [busy, setBusy] = useState(false);
    const [erreur, setErreur] = useState(null);
    const envoi = useRef(false);
    const champ = useRef(null);

    useEffect(() => {
        if (valide && champ.current) champ.current.focus();
    }, [valide]);

    if (!valide) return <PanierRefuse erreurs={erreurs} onRetourVente={onRetourVente} />;

    const t = panier.totals;
    const du = arrondi.amount_due_cents;
    const monnaie = monnaieARendre(du, recu);
    const peutTerminer = monnaie !== null && !busy;

    const chiffres = (c) => {
        // Apres un montant rapide, la saisie au pave repart de zero.
        setRecu((v) => ajouterChiffres(rapide === null ? v : 0, c, MONTANT_RECU_MAX_CENTS));
        setRapide(null);
    };
    const effacer = () => {
        setRecu((v) => (rapide === null ? effacerChiffre(v) : 0));
        setRapide(null);
    };
    const choisir = (cents, cle) => {
        setRecu(cents);
        setRapide(cle);
        if (champ.current) champ.current.focus();
    };

    const terminer = async () => {
        if (envoi.current || monnaieARendre(du, recu) === null) return;
        envoi.current = true;
        setBusy(true);
        setErreur(null);
        try {
            const { vente } = await recordSale(db, { panier, shiftId, cashReceivedCents: recu, ...(now ? { now } : {}) });
            onVenteTerminee(vente);
        } catch (error) {
            envoi.current = false;
            setBusy(false);
            setErreur(messageErreurVente(error));
        }
    };

    return (
        <Box data-testid="ecran-paiement" sx={{ flex: 1, display: 'flex', gap: 2, p: 2, minHeight: 0 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box sx={CARD_SX}>
                    <Typography sx={LABEL_SX}>TOTAL À PAYER (comptant)</Typography>
                    <Typography data-testid="total-a-payer" data-cents={du} sx={{ fontSize: 64, fontWeight: 800, lineHeight: 1, color: palette.text }}>
                        {formatMontant(du)}
                    </Typography>
                    <Box data-testid="arrondi-comptant" data-cents={arrondi.cash_rounding_cents} sx={{ mt: 1.5, fontSize: 18, bgcolor: '#F1F5F9', borderRadius: '10px', px: 1.75, py: 1.25, color: palette.text }}>
                        {arrondi.cash_rounding_cents === 0 ? (
                            <>Total {formatMontant(t.total_cents)} · aucun arrondi comptant</>
                        ) : (
                            <>
                                Total {formatMontant(t.total_cents)} → <b>{formatMontant(du)}</b>
                                {' · arrondi comptant '}
                                {arrondi.cash_rounding_cents > 0 ? '+' : ''}{formatMontant(arrondi.cash_rounding_cents)}
                            </>
                        )}
                    </Box>
                    <Typography sx={{ mt: 1.5, fontSize: 16, color: palette.textSecondary }}>
                        {t.item_count} article{t.item_count > 1 ? 's' : ''} · TPS {formatMontant(t.tps_cents)} · TVQ {formatMontant(t.tvq_cents)}
                    </Typography>
                </Box>

                <Box role="group" aria-label="Montants rapides" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {[['exact', 'Montant exact', du], ...BILLETS.map((c) => [String(c), `${c / 100} $`, c])].map(([cle, libelle, cents]) => {
                        const choisi = rapide === cle;
                        return (
                            <Button
                                key={cle}
                                variant="outlined"
                                aria-pressed={choisi}
                                disabled={busy}
                                onClick={() => choisir(cents, cle)}
                                sx={{
                                    ...TILE_SX,
                                    color: choisi ? palette.primaryDark : palette.text,
                                    borderColor: choisi ? palette.primary : palette.border,
                                    bgcolor: choisi ? '#EFF6FF' : palette.surface,
                                }}
                            >
                                {libelle}
                            </Button>
                        );
                    })}
                </Box>

                {monnaie !== null ? (
                    <Box data-testid="monnaie" data-cents={monnaie} role="status" sx={{ ...CARD_SX, bgcolor: palette.successTint, border: `2px solid ${palette.success}` }}>
                        <Typography sx={{ ...LABEL_SX, color: palette.successOnTint }}>MONNAIE À RENDRE</Typography>
                        <Typography sx={{ fontSize: 56, fontWeight: 800, lineHeight: 1.1, color: palette.successOnTint }}>
                            {formatMontant(monnaie)}
                        </Typography>
                    </Box>
                ) : (
                    recu > 0 && (
                        <Box data-testid="manque" role="status" sx={{ ...CARD_SX, bgcolor: palette.warningTint, border: `2px solid ${palette.warning}`, color: palette.warningOnTint }}>
                            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                                Montant reçu insuffisant : il manque {formatMontant(du - recu)}
                            </Typography>
                        </Box>
                    )
                )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                    <ChampMontant
                        ref={champ}
                        id="montant-recu"
                        testId="montant-recu"
                        label="Montant reçu"
                        cents={recu}
                        disabled={busy}
                        onChiffres={chiffres}
                        onEffacer={effacer}
                        onValider={terminer}
                    />
                    <PaveMontant label="Pavé du montant reçu" disabled={busy} onChiffres={chiffres} onEffacer={effacer} />
                </Box>
                {erreur && (
                    <Typography role="alert" data-testid="paiement-erreur" sx={{ fontSize: 18, fontWeight: 600, color: palette.errorOnTint, bgcolor: palette.errorTint, p: 1.5, borderRadius: 2 }}>
                        {erreur}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    disabled={!peutTerminer}
                    onClick={terminer}
                    sx={{ minHeight: 88, fontSize: 26, fontWeight: 800, borderRadius: '12px' }}
                >
                    TERMINER LA VENTE
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    disabled={busy}
                    onClick={onRetourVente}
                    sx={{ minHeight: 64, fontSize: 22, borderRadius: '12px', borderWidth: 2, color: palette.text, borderColor: palette.border, '&:hover': { borderWidth: 2 } }}
                >
                    Retour au panier
                </Button>
            </Box>
        </Box>
    );
}
